// Copyright (c) StrangeBee 2024

import React, { ReactElement, useCallback, useContext, useMemo, useState } from 'react';
import { classList, useLocalMessage } from '@Helpers/';
import { FormInstance } from 'antd/lib/form';
import { THForm } from '@Molecules/THForm/THForm';
import { FormSubmitButton } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import { BulkCaseTemplateParams, CaseTemplateOutput, PageTemplateOutput, TaskOutput } from 'thehive-sdk';
import { THCheckbox } from '@Molecules/Inputs/THCheckbox/THCheckbox';
import { THSecurityTag } from '@Molecules/Inputs/THSecurityTag/THSecurityTag';
import DisplayTag from '@Organisms/DisplayTag/DisplayTag';
import { ScopeContext } from '@Context/ScopeProvider';
import { THMarkdown } from '@Molecules/Inputs/THMarkdown/THMarkdown';
import { THInput } from '@Molecules/Inputs/THInput/THInput';
import client from '@Client';
import useListQuery from '@Hooks/useListQuery';
import { ICONS } from '@Constants/iconsApp';
import THSelector from '@Molecules/THSelector/THSelector';
import { DisplayCustomFields } from '@Organisms/DisplayCustomFields/DisplayCustomFields';
import { THSelect } from '@Molecules/Inputs/THSelect/THSelect';

import './CaseBulkCaseTemplateForm.scss';
import {
    PageTemplateExisting,
    PageTemplateFormated,
} from '@Organisms/PageTemplate/PageTemplateExisting/PageTemplateExisting';
import { PageTemplateFormValues } from '@Organisms/PageTemplate/PageTemplateForm/PageTemplateForm';

interface CaseBulkCaseTemplateFormProps {
    onFinish: (values: Partial<BulkCaseTemplateParams>) => void;
    form: FormInstance;
    isValid: boolean;
    onChange: () => void;
    onCancel: () => void;
    organisation: string;
}

type TemplateDataType = Partial<CaseTemplateOutput>;
type CaseTemplateListType = Record<string, TemplateDataType>;

interface BulkCaseTemplateFormProps extends BulkCaseTemplateParams {
    importTasksObject: TaskOutput[];
}

const remove =
    (item: any) =>
    (e: any): boolean =>
        e !== item;

export const CaseBulkCaseTemplateForm = ({
    onFinish,
    form,
    isValid,
    onChange,
    onCancel,
    organisation = '',
}: CaseBulkCaseTemplateFormProps): ReactElement => {
    const i = useLocalMessage();
    const { coloredTags } = useContext(ScopeContext);
    const [selectedTasks, setSelectedTasks] = useState<TaskOutput[]>([]);
    const [countSelected, setCountSelected] = useState(0);
    const [activeAttribute, setActiveAttribute] = useState<Partial<BulkCaseTemplateParams>>();

    const { data: data } = useListQuery<CaseTemplateOutput[]>({
        queryParams: {
            selector: {
                head: [{ name: 'getOrganisation', idOrName: organisation }, { name: 'caseTemplates' }],
                name: `get-case-templates-${organisation}`,
                extraData: ['pageTemplateHeaders'],
            },
            sort: ['+name'],
            paginationOptions: {
                itemsPerPage: 999,
                page: 0,
            },
        },
        controller: client.CaseTemplateController,
    });

    const caseTemplateList: CaseTemplateListType = useMemo(() => {
        const result = (data || []).reduce((acc, cur) => {
            acc[cur._id] = cur;

            return acc;
        }, {} as CaseTemplateListType);

        return result;
    }, [data]);

    const onSelectAllTasks = useCallback(
        (allTask: TaskOutput[]) => (): void => {
            setCountSelected(allTask.length);
            setSelectedTasks(allTask);
        },
        [],
    );

    const onClickSelectTask = useCallback(
        (item: TaskOutput) => {
            const list = selectedTasks || [];
            const action = selectedTasks?.includes(item) ? 'remove' : 'add';
            const newValues = action === 'remove' ? selectedTasks?.filter(remove(item)) : [...list, item];

            setCountSelected(newValues.length);
            setSelectedTasks(newValues);
        },
        [selectedTasks],
    );

    const handlerOnchange = useCallback(
        (e: any) => {
            const [name, value] = Object.entries(e)[0];
            setActiveAttribute({ ...activeAttribute, [name]: value });
            onChange();
        },
        [onChange, activeAttribute],
    );

    const handlerOnFinish = useCallback(
        (values: Partial<BulkCaseTemplateFormProps>) => {
            // @ts-expect-error: pageTemplates is sent through form structure and does not exist in Partial<BulkCaseTemplateFormProps>, so we format it the right way
            values.importPages = values.pageTemplates?.reduce((acc: string[], curr: PageTemplateOutput) => {
                if (curr._type !== '') {
                    acc.push(curr._id);
                } else {
                    const getAllTemplatesFromCategory = caseTemplateList[
                        values.caseTemplate as string
                    ]?.extraData?.pageTemplateHeaders.reduce((ids: string[], item: PageTemplateOutput) => {
                        if (item.category === curr._id) {
                            ids.push(item._id);
                        }
                        return ids;
                    }, []);
                    acc = [...acc, getAllTemplatesFromCategory].flat();
                }
                return acc;
            }, []);

            // @ts-expect-error: after format values.importPages, remove pageTemplates property
            delete values.pageTemplates;
            onFinish({
                ...values,
                importTasks: selectedTasks?.map(({ _id }) => _id),
            });
        },
        [selectedTasks, onFinish, caseTemplateList],
    );

    return (
        <THForm<Partial<BulkCaseTemplateFormProps>>
            name="case-bulk-caseTemplate__form"
            onValuesChange={handlerOnchange}
            form={form}
            onFinish={handlerOnFinish}
            initialValues={{
                updateCustomFields: false,
                updateDescription: false,
                updatePap: false,
                updateSeverity: false,
                updateTags: false,
                updateTitlePrefix: false,
                updateTlp: false,
            }}
        >
            <THForm.Layout
                footer={<FormSubmitButton onCancel={onCancel} disabled={!isValid} footerText={i('general.confirm')} />}
            >
                <THForm.Item label={i('case.bulk-caseTemplate.form.caseTemplate')} name="caseTemplate" required>
                    <THSelect
                        showSearch
                        options={Object.values(caseTemplateList).map(({ name, displayName, _id }) => ({
                            label: displayName || name,
                            value: _id,
                        }))}
                        filterOption={(value, option) => {
                            const label = (option?.label as string) || '';

                            return label?.toLowerCase().includes(value?.toLowerCase());
                        }}
                    />
                </THForm.Item>

                <THForm.Item<Partial<BulkCaseTemplateParams & PageTemplateFormValues>>
                    noStyle
                    shouldUpdate={(prev, curr) => prev.caseTemplate !== curr.caseTemplate}
                >
                    {({ getFieldValue }) => {
                        const caseTemplate = getFieldValue('caseTemplate');

                        const sortTemplatesArray = (templates: PageTemplateFormated[]) => {
                            templates.sort(function (a, b) {
                                // Compare categories
                                const categorieComparison = a.category.localeCompare(b.category);
                                if (categorieComparison !== 0) {
                                    return categorieComparison;
                                }
                                // If the categories are identical, compare the titles
                                return a.title.localeCompare(b.title);
                            });
                            return Array.from(new Set(templates));
                        };

                        const allCategories = Array.from(
                            new Set(
                                caseTemplateList[caseTemplate]?.extraData?.pageTemplateHeaders.reduce(
                                    (acc: string[], { category }: { category: string }) =>
                                        acc.includes(category) ? acc : [...acc, category],
                                    [],
                                ),
                            ),
                        ) as string[];

                        const pageTemplateListFormated = () => {
                            const sortedPageTemplateList = sortTemplatesArray(
                                caseTemplateList[caseTemplate]?.extraData?.pageTemplateHeaders,
                            );

                            allCategories.forEach((category: string) => {
                                const firstOfCategory = sortedPageTemplateList.findIndex(
                                    (elt) => elt.category === category,
                                );
                                sortedPageTemplateList.splice(firstOfCategory, 0, {
                                    title: category,
                                    category,
                                    isCategory: true,
                                    _id: category,
                                    id: '',
                                    createdBy: '',
                                    createdAt: 0,
                                    content: '',
                                    _type: '',
                                    slug: '',
                                    order: 0,
                                    updatedAt: 0,
                                });
                            });
                            return Array.from(new Set(sortedPageTemplateList));
                        };

                        const tasks = caseTemplateList[caseTemplate]?.tasks?.sort(
                            ({ order: orderA }, { order: orderB }) => orderA - orderB,
                        );

                        return (
                            <div className="case-bulk-caseTemplate__wrapper">
                                <div
                                    className={classList('case-bulk-caseTemplate__field', {
                                        active: activeAttribute?.updateTags,
                                    })}
                                >
                                    <THForm.Item
                                        horizontal
                                        hidden={!caseTemplate || !caseTemplateList[caseTemplate]?.tags?.length}
                                        label={i('case.bulk-caseTemplate.form.tags')}
                                        name="updateTags"
                                        valuePropName="checked"
                                    >
                                        <THCheckbox />
                                    </THForm.Item>
                                    {!caseTemplateList[caseTemplate]?.tags?.length ? null : (
                                        <div className="case-bulk-caseTemplate__field-preview">
                                            <div className="case-list-title__tags">
                                                <DisplayTag.List<string>
                                                    data={caseTemplateList[caseTemplate]?.tags || []}
                                                >
                                                    {(tag) => <DisplayTag tag={tag} color={coloredTags[tag]} />}
                                                </DisplayTag.List>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div
                                    className={classList('case-bulk-caseTemplate__field', {
                                        active: activeAttribute?.updateCustomFields,
                                    })}
                                >
                                    <THForm.Item
                                        horizontal
                                        hidden={!caseTemplate || !caseTemplateList[caseTemplate]?.customFields?.length}
                                        label={i('case.bulk-caseTemplate.form.customFields')}
                                        name="updateCustomFields"
                                        valuePropName="checked"
                                    >
                                        <THCheckbox />
                                    </THForm.Item>
                                    {!caseTemplateList[caseTemplate]?.customFields?.length ? null : (
                                        <div className="case-bulk-caseTemplate__field-preview">
                                            <div className="case-list-title__custom-fields">
                                                <DisplayCustomFields
                                                    forceDisplay={true}
                                                    customFields={caseTemplateList[caseTemplate]?.customFields || []}
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div
                                    className={classList('case-bulk-caseTemplate__field', {
                                        active: activeAttribute?.importTasks,
                                    })}
                                >
                                    <THForm.Item
                                        label={
                                            i('case.bulk-caseTemplate.form.importTasks') +
                                            (countSelected > 0 ? `(${countSelected})` : ``)
                                        }
                                        hidden={!caseTemplate || !tasks?.length}
                                        extraAction={
                                            <>
                                                <span
                                                    className="case-bulk-caseTemplate__field-extra-action"
                                                    onClick={onSelectAllTasks(tasks || [])}
                                                >
                                                    {i('common.select-all')}
                                                </span>
                                                <span
                                                    className="case-bulk-caseTemplate__field-extra-action"
                                                    onClick={onSelectAllTasks([])}
                                                >
                                                    {i('common.deselect-all')}
                                                </span>
                                            </>
                                        }
                                    >
                                        <THSelector<TaskOutput>
                                            dataSource={tasks || []}
                                            refKey="_id"
                                            onChange={undefined}
                                            value={selectedTasks}
                                            customSelect={(item, value) => value.includes(item)}
                                            onClick={onClickSelectTask}
                                            filtering={{
                                                search: ({ description, group, title }): string =>
                                                    `${title} - ${group} - ${description}`,
                                                placeholder: i('case.bulk-caseTemplate.form.filter-placeholder'),
                                            }}
                                        >
                                            {({ group, title, description }, selected): ReactElement => (
                                                <div className={classList('case-bulk-caseTemplate-task__item')}>
                                                    <span className="item-select">
                                                        {selected ? (
                                                            <ICONS.CheckCircleTwoTone />
                                                        ) : (
                                                            <ICONS.MinusCircleOutlined />
                                                        )}
                                                        <span className="item-select__title">
                                                            {`${group} - ${title}`}
                                                        </span>

                                                        {description ? (
                                                            <span className="item-select__desc">{description}</span>
                                                        ) : null}
                                                    </span>
                                                </div>
                                            )}
                                        </THSelector>
                                    </THForm.Item>
                                </div>

                                <div
                                    className={classList('case-bulk-caseTemplate__field', {
                                        active: activeAttribute?.updateTitlePrefix,
                                    })}
                                >
                                    <THForm.Item
                                        horizontal
                                        hidden={
                                            !caseTemplate ||
                                            !caseTemplateList[caseTemplate]?.titlePrefix ||
                                            caseTemplateList[caseTemplate]?.titlePrefix?.trim() === ''
                                        }
                                        label={i('case.bulk-caseTemplate.form.titlePrefix')}
                                        name="updateTitlePrefix"
                                        valuePropName="checked"
                                    >
                                        <THCheckbox />
                                    </THForm.Item>
                                    {caseTemplateList[caseTemplate]?.titlePrefix?.trim() ? (
                                        <div className="case-bulk-caseTemplate__field-preview type-text">
                                            <THInput
                                                borderless
                                                value={caseTemplateList[caseTemplate]?.titlePrefix}
                                                disabled={true}
                                            />
                                        </div>
                                    ) : null}
                                </div>

                                <div
                                    className={classList('case-bulk-caseTemplate__field', {
                                        active: activeAttribute?.updateDescription,
                                    })}
                                >
                                    <THForm.Item
                                        horizontal
                                        hidden={
                                            !caseTemplate ||
                                            !caseTemplateList[caseTemplate]?.description ||
                                            caseTemplateList[caseTemplate]?.description?.trim() === ''
                                        }
                                        label={i('case.bulk-caseTemplate.form.description')}
                                        name="updateDescription"
                                        valuePropName="checked"
                                    >
                                        <THCheckbox />
                                    </THForm.Item>
                                    {caseTemplateList[caseTemplate]?.description?.trim() ? (
                                        <div className="case-bulk-caseTemplate__field-preview  type-text">
                                            <THMarkdown
                                                value={caseTemplateList[caseTemplate]?.description}
                                                borderless
                                                disabled={true}
                                            />
                                        </div>
                                    ) : null}
                                </div>

                                <div
                                    className={classList('case-bulk-caseTemplate__field', {
                                        active: activeAttribute?.updateSeverity,
                                    })}
                                >
                                    <THForm.Item
                                        horizontal
                                        hidden={!caseTemplate || caseTemplateList[caseTemplate]?.severity === undefined}
                                        label={i('case.bulk-caseTemplate.form.severity')}
                                        name="updateSeverity"
                                        valuePropName="checked"
                                    >
                                        <THCheckbox />
                                    </THForm.Item>
                                    {caseTemplateList[caseTemplate]?.severity !== undefined ? (
                                        <div className="case-bulk-caseTemplate__field-preview">
                                            <THSecurityTag
                                                type={'severity'}
                                                disabled={true}
                                                value={caseTemplateList[caseTemplate]?.severity}
                                            />
                                        </div>
                                    ) : null}
                                </div>

                                <div
                                    className={classList('case-bulk-caseTemplate__field', {
                                        active: activeAttribute?.updateTlp,
                                    })}
                                >
                                    <THForm.Item
                                        horizontal
                                        hidden={!caseTemplate || caseTemplateList[caseTemplate]?.tlp === undefined}
                                        label={i('case.bulk-caseTemplate.form.tlp')}
                                        name="updateTlp"
                                        valuePropName="checked"
                                    >
                                        <THCheckbox />
                                    </THForm.Item>
                                    {caseTemplateList[caseTemplate]?.tlp !== undefined ? (
                                        <div className="case-bulk-caseTemplate__field-preview">
                                            <THSecurityTag
                                                type={'tlp'}
                                                disabled={true}
                                                value={caseTemplateList[caseTemplate]?.tlp}
                                            />
                                        </div>
                                    ) : null}
                                </div>

                                <div
                                    className={classList('case-bulk-caseTemplate__field', {
                                        active: activeAttribute?.updatePap,
                                    })}
                                >
                                    <THForm.Item
                                        horizontal
                                        hidden={!caseTemplate || caseTemplateList[caseTemplate]?.pap === undefined}
                                        label={i('case.bulk-caseTemplate.form.pap')}
                                        name="updatePap"
                                        valuePropName="checked"
                                    >
                                        <THCheckbox />
                                    </THForm.Item>
                                    {caseTemplateList[caseTemplate]?.pap !== undefined ? (
                                        <div className="case-bulk-caseTemplate__field-preview">
                                            <THSecurityTag
                                                type={'pap'}
                                                disabled={true}
                                                value={caseTemplateList[caseTemplate]?.pap}
                                            />
                                        </div>
                                    ) : null}
                                </div>

                                {caseTemplateList[caseTemplate]?.extraData?.pageTemplateHeaders.length > 0 && (
                                    <THForm.Item
                                        name="pageTemplates"
                                        className="case-bulk-caseTemplate-page-templates__item"
                                    >
                                        <PageTemplateExisting
                                            required={false}
                                            noFooter
                                            noForm
                                            disabled={true}
                                            form={form}
                                            pageTemplateList={pageTemplateListFormated()}
                                            onFinish={(values: PageTemplateFormValues) => {
                                                handlerOnFinish({
                                                    caseTemplate: caseTemplateList[caseTemplate]._id,
                                                    importPages: values.pageTemplates
                                                        ?.map((template) => template._id)
                                                        .filter((item) => item !== ''),
                                                });
                                            }}
                                            onValuesChange={handlerOnchange}
                                            enableSelectAll
                                        />
                                    </THForm.Item>
                                )}
                            </div>
                        );
                    }}
                </THForm.Item>
            </THForm.Layout>
        </THForm>
    );
};
